import { traceEvent } from '@wk/wk-gatherer';
import classNames from 'classnames';
import router from 'next/router';
import React, { FC, useContext } from 'react';

import Mobile from '@/components/Device/Mobile';
import Pc from '@/components/Device/PC';
// eslint-disable-next-line import/no-cycle
import ResourceContext, { Tag } from '@/components/Resources/context';
import useIsMobile from '@/hooks/useIsMobile';

const containCurrentValue = (tags: Tag[], currentTag: Tag) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < tags.length; i++) {
    const item = tags[i];
    if (
      item.value === currentTag.value &&
      item.parentValue === currentTag.parentValue
    ) {
      return true;
    }
  }
  return false;
};

// 判断移动端该父级下是否具有子集标签，如果有则父级标红
const containCurrentMobileValue = (mobileTags: Tag[], currentTags: Tag[]) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < mobileTags.length; i++) {
    const item = mobileTags[i];
    if (currentTags.some(tag => item.value === tag.value)) {
      return true;
    }
  }

  return false;
};

interface Props {
  label: string;
  value: string;
  subMenu: Tag[];
  mobileTags: Tag[];
  pushMobileTag: (tag: Tag) => void;
  deleteMobileTag: (tag: Tag) => void;
  openMenuKeys: string[];
  changeOpen: (key: string) => void;
}

const MenuItemCard: FC<Props> = ({
  label: parentLabel,
  value: parentKey,
  subMenu,
  mobileTags,
  pushMobileTag,
  deleteMobileTag,
  openMenuKeys,
  changeOpen,
}) => {
  const {
    filterTags,
    pushFilterTagAndUpdatePath,
    deleteFilterTagAndUpdatePath,
  } = useContext(ResourceContext);
  const isOpen = openMenuKeys?.includes?.(parentKey);
  const isMobile = useIsMobile();

  return (
    <>
      <div
        onClick={() => changeOpen(parentKey)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            changeOpen(parentKey);
          }
        }}
        role="button"
        tabIndex={0}
        className={classNames(
          'flex cursor-pointer items-center justify-between font-bold',
          containCurrentMobileValue(mobileTags, subMenu) && isMobile
            ? 'text-[#FF5353]'
            : '',
        )}>
        <span>{parentLabel}</span>
        <svg
          className={classNames(
            isOpen ? 'rotate-180' : '',
            'transition-transform duration-300',
          )}
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none">
          <path
            d="M11.9824 6.85715L8.34587 10.4937C8.12272 10.7169 7.76091 10.7169 7.53775 10.4937L3.9012 6.85715"
            stroke="#484C70"
            strokeWidth="1.71429"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <li className="relative mb-5 border-b-[1px] border-solid border-[#EBECFA] font-semibold text-[#1B1F4D] last:mb-0 last:border-b-0">
        <div
          className={classNames('overflow-hidden pb-5 transition-[height] duration-300',{
            'pt-5': isOpen,
          })}
          style={{
            height: isOpen ? 'auto' : 0,
            opacity: isOpen ? 1 : 0,
          }}>
          <Pc>
            <div className="flex flex-wrap gap-3">
              {subMenu.map(item => {
                const isSelected = containCurrentValue(filterTags, item);
                return (
                  <div
                    onClick={() => {
                      traceEvent({
                        data: {
                          _event: 'O_LearningWorldList_MenuClick',
                          subject: router?.query?.subject,
                          options: item.label,
                        },
                      });
                      if (isSelected) {
                        deleteFilterTagAndUpdatePath(item);
                      } else {
                        pushFilterTagAndUpdatePath(item);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        traceEvent({
                          data: {
                            _event: 'O_LearningWorldList_MenuClick',
                            subject: router?.query?.subject,
                            options: item.label,
                          },
                        });
                        if (isSelected) {
                          deleteFilterTagAndUpdatePath(item);
                        } else {
                          pushFilterTagAndUpdatePath(item);
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    key={item.value}
                    className={classNames(
                      'relative flex max-w-[200px] cursor-pointer items-center justify-center rounded-lg border px-[14px] py-2 text-sm font-normal transition-colors duration-200',
                      isSelected
                        ? 'border-wk-red-500 bg-[#FFF5F5] text-wk-red-500'
                        : 'border-wk-border-3 text-wk-words-1 hover:border-[#FFCFD5] hover:bg-[#FFF5F5]',
                    )}>
                    <span className="line-clamp-1 text-center">
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </Pc>
          <Mobile>
            <div className="flex flex-wrap gap-3">
              {subMenu.map(item => {
                const isSelected = containCurrentValue(mobileTags, item);
                return (
                  <div
                    onClick={() => {
                      traceEvent({
                        data: {
                          _event: 'O_LearningWorldList_MenuClick',
                          subject: router?.query?.subject,
                          options: item.label,
                        },
                      });
                      if (isSelected) {
                        deleteMobileTag(item);
                      } else {
                        pushMobileTag(item);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        traceEvent({
                          data: {
                            _event: 'O_LearningWorldList_MenuClick',
                            subject: router?.query?.subject,
                            options: item.label,
                          },
                        });
                        if (isSelected) {
                          deleteMobileTag(item);
                        } else {
                          pushMobileTag(item);
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    key={item.value}
                    className={classNames(
                      'relative flex max-w-full cursor-pointer items-center justify-center rounded-lg border px-[14px] py-2 text-sm font-normal transition-colors duration-200',
                      isSelected
                        ? 'border-wk-red-500 bg-[#FFF5F5] text-wk-red-500'
                        : 'border-wk-border-3 text-wk-words-1',
                    )}>
                    <span className="line-clamp-1 text-center">
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </Mobile>
        </div>
      </li>
    </>
  );
};

export default MenuItemCard;
